import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AssignmentLate,
  Assignment,
  Person,
  Timeline,
  Dashboard as DashboardIcon,
  ExpandMore,
  Assessment,
  TrendingUp,
} from "@mui/icons-material";
import { useAuth } from '../context/AuthContext';

const reportPaths = ['/promises', '/reports/arrears-aging', '/reports/agent-performance'];

const Sidebar = () => {
  const location = useLocation();
  const { navigationConfig, isManager } = useAuth();

  const [reportsOpen, setReportsOpen] = useState(
    reportPaths.some(p => location.pathname === p)
  );

  // Icon mapping for dynamic navigation
  const iconMap = {
    'Dashboard': <DashboardIcon />,
    'AssignmentLate': <AssignmentLate />,
    'Assignment': <Assignment />,
    'Person': <Person />,
    'Timeline': <Timeline />,
  };

  // Main menu items (without Promises - moved to Reports dropdown)
  const defaultMenu = [
    ...(isManager ? [{
      id: "all-late",
      label: "All late Loans",
      icon: <AssignmentLate />,
      path: "/all-late-loans",
    }] : []),
    ...(isManager ? [{
      id: "unassigned",
      label: "Unassigned Loans",
      icon: <Assignment />,
      path: "/unassigned-loans",
    }] : []),
    {
      id: "assigned",
      label: "Assigned Loans",
      icon: <Person />,
      path: "/assigned-loans",
    },
  ];

  // Reports sub-items
  const reportSubItems = [
    {
      id: "promises",
      label: "Promises & Resolutions",
      icon: <Timeline />,
      path: "/promises",
    },
    {
      id: "arrears-aging",
      label: "Arrears Aging",
      icon: <Assessment />,
      path: "/reports/arrears-aging",
    },
    {
      id: "agent-performance",
      label: "Agent Performance",
      icon: <TrendingUp />,
      path: "/reports/agent-performance",
    },
  ];

  const menuItems = (navigationConfig?.sidebarNav?.map((item, index) => ({
    id: `menu-${index}`,
    label: item.label,
    icon: iconMap[item.icon] || <DashboardIcon />,
    path: item.path,
  })) || defaultMenu).filter(item => !reportPaths.includes(item.path));

  const isActive = (path) => {
    if (path === "/dashboard") {
      return location.pathname === "/" || location.pathname === "/dashboard";
    }
    return location.pathname === path;
  };

  const isReportActive = reportPaths.some(p => location.pathname === p);

  return (
    <div className="sidebar bg-white border-r border-gray-200 h-full">
      <div className="p-4">
        <h3 className="uppercase mb-5 font-bold text-gray-700 text-sm">
          Collections Menu
        </h3>
        <ul className="space-y-1">
          {menuItems.map((item) => {
            const active = isActive(item.path);
            return (
              <li key={item.id}>
                <Link
                  to={item.path}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    active
                      ? "bg-blue-50 text-blue-600 font-medium"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span
                    className={
                      active ? "text-blue-600" : "text-gray-500"
                    }
                  >
                    {item.icon}
                  </span>
                  <span className="text-sm">{item.label}</span>
                </Link>
              </li>
            );
          })}

          {/* Reports Dropdown */}
          <li>
            <button
              onClick={() => setReportsOpen(!reportsOpen)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isReportActive
                  ? "bg-blue-50 text-blue-600 font-medium"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span className={isReportActive ? "text-blue-600" : "text-gray-500"}>
                <Assessment />
              </span>
              <span className="text-sm flex-1 text-left">Reports</span>
              <ExpandMore
                className={isReportActive ? "text-blue-600" : "text-gray-400"}
                style={{
                  transform: reportsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  fontSize: 20
                }}
              />
            </button>

            {reportsOpen && (
              <ul className="mt-1 space-y-1">
                {reportSubItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <li key={item.id}>
                      <Link
                        to={item.path}
                        className={`w-full flex items-center gap-3 pl-10 pr-4 py-2.5 rounded-lg transition-colors ${
                          active
                            ? "bg-blue-50 text-blue-600 font-medium"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <span
                          className={active ? "text-blue-600" : "text-gray-400"}
                          style={{ fontSize: 18 }}
                        >
                          {item.icon}
                        </span>
                        <span className="text-sm">{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
